export class ContactsAPI {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  // Upload patient document(s)
  async uploadPatientDocument(formData) {
    // formData should be a FormData object with files and metadata
    return this.apiClient.post("/api/v1/contacts/upload-documents", formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  async patientRegistration(formData) {
    return this.apiClient.post("api/v1/contacts/patient-registration", formData);
  }

  async getPatientDetailsByExternalCode(patientCode) {
    return this.apiClient.get(`/api/v1/contacts/details/${patientCode}`);  
  }

  async getContacts() {
    return this.apiClient.get("/contacts");
  }

  async getContactById(contactId) {
    return this.apiClient.get(`/contacts/${contactId}`);
  }

  async createContact(contactData) {
    return this.apiClient.post("/contacts", contactData);
  }

  async updateContact(contactId, contactData) {
    return this.apiClient.put(`/contacts/${contactId}`, contactData);
  }

  async editContact(contactId, contactData) {
    return this.apiClient.patch(`/api/v1/contacts/edit/${contactId}`, contactData);
  }

  async deleteContact(contactId) {
    return this.apiClient.delete(`/contacts/${contactId}`);
  }

  async getDoctorsList() {
    return this.apiClient.get("/api/v1/contacts/get-contacts/doctor");
  }

  // Change the assigned doctor for a patient (applies to spouse too)
  async changeDoctor(contactId, doctorId) {
    return this.apiClient.patch(`/api/v1/contacts/${contactId}/doctor`, { doctor_id: doctorId });
  }

  // Full change history (profile edits, phone/address, doctor changes) for a contact
  async getContactAuditLogs(contactId, page = 1, limit = 15) {
    return this.apiClient.get(`/api/v1/contacts/${contactId}/audit-logs?page=${page}&limit=${limit}`);
  }

  async getPatientParams() {
    return this.apiClient.get("/api/v1/contacts/params/patient-params");
  }

  async searchContacts(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/contacts/search?${queryString}`);
  }

  // Dashboard widget — new-registration counts by gender, optional country
  // filter, caller-resolved date window.
  async getRegistrationStats({ country_id, date_from, date_to }) {
    const params = new URLSearchParams({ created_from: date_from, created_to: date_to });
    if (country_id) params.set('country_id', country_id);
    return this.apiClient.get(`/api/v1/contacts/dashboard/registration-stats?${params.toString()}`);
  }

  async getPatientDetails(patientCode) {
    return this.apiClient.get(`/api/v1/contacts/details/${patientCode}`);
  }

  // Get existing documents for a patient
  async getPatientDocuments(patientCode) {
    // TODO: Replace with your actual API endpoint
    return this.apiClient.get(`api/v1/contacts/details/${patientCode}`);
  }
  
  async getAllVisitsByContact(contactId) {
    return this.apiClient.get(`/api/v1/visits/contact/${contactId}/all`);
  }

  // Get document types
  async getDocumentTypes() {
    return this.apiClient.get('/api/v1/contacts/document-types');
  }

  // User registration
  async userRegistration(userData) {
    return this.apiClient.post('/api/v1/contacts/user-registration', userData);
  }

  // Get users list with pagination
  async getUsersList(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/contacts/users-list?${queryString}`);
  }

  // Update user status (active/inactive)
  async updateUserStatus(userId, isActive) {
    return this.apiClient.patch(`/api/v1/contacts/update-user-status/${userId}`, {
      is_active: isActive,
    });
  }

  // Update per-user OTP/2FA login toggle
  async updateOtpStatus(userId, isEnabled) {
    return this.apiClient.patch(`/api/v1/contacts/update-otp-status/${userId}`, {
      is_otp_login_enabled: isEnabled,
    });
  }

  // Get my profile
  async getMyProfile() {
    return this.apiClient.get('/api/v1/contacts/my-profile');
  }

  // Update my profile
  async updateMyProfile(data) {
    return this.apiClient.patch('/api/v1/contacts/my-profile', data);
  }

  // ── Donor ────────────────────────────────────────────────────────

  async registerDonor(data) {
    return this.apiClient.post('/api/v1/donors/register', data);
  }

  async searchDonors(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/donors/search?${queryString}`);
  }

  async getDonorDetails(external_code) {
    return this.apiClient.get(`/api/v1/donors/details/${external_code}`);
  }

  async editDonor(donorId, data) {
    return this.apiClient.patch(`/api/v1/donors/edit/${donorId}`, data);
  }

  async getDonorInvestigationParams(donorId) {
    return this.apiClient.get(`/api/v1/donors/investigation-params/${donorId}`);
  }

  async getDonorDocumentTypes() {
    return this.apiClient.get('/api/v1/donors/document-types');
  }

  async uploadDonorProfileImage(formData) {
    // Do NOT set Content-Type manually — browser must set it with the multipart boundary
    return this.apiClient.post('/api/v1/donors/upload-profile-image', formData);
  }

  async uploadDonorDocuments(formData) {
    // Do NOT set Content-Type manually — browser must set it with the multipart boundary
    return this.apiClient.post('/api/v1/donors/upload-documents', formData);
  }

  // ── Old Patient Registration ─────────────────────────────────────────────

  async checkOldPatientMobile(mobile) {
    return this.apiClient.get(`/api/v1/old-patients/check-mobile?mobile=${encodeURIComponent(mobile)}`);
  }

  async registerOldPatient(data) {
    return this.apiClient.post('/api/v1/old-patient-registration', data);
  }

  async searchOldPatients(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/old-patients?${queryString}`);
  }

  async getOldPatientByCode(externalCode) {
    return this.apiClient.get(`/api/v1/old-patients/${externalCode}`);
  }
}
